/**
 * Help Tooltip Component
 * Sprint 12 Phase 3 - Financial UX Polish
 * 
 * Provides contextual help tooltips for financial terms
 */

import React, { useState } from 'react';
import { HelpCircle } from 'lucide-react';

// Financial help content
export const FINANCIAL_HELP = {
  capex: {
    title: 'CAPEX (Capital Expenditure)',
    description: 'One-time costs for acquiring or upgrading physical assets, software licenses, or infrastructure. These are typically depreciated over time.',
    examples: ['Hardware purchases', 'Software licenses (perpetual)', 'Infrastructure buildout', 'Initial implementation costs']
  },
  opex: {
    title: 'OPEX (Operating Expenditure)',
    description: 'Recurring operational costs for running the business day-to-day. These expenses are fully deductible in the year they occur.',
    examples: ['SaaS subscriptions', 'Cloud hosting', 'Maintenance contracts', 'Ongoing support costs', 'Labor costs']
  },
  labor: {
    title: 'Labor Costs',
    description: 'Costs associated with human resources needed for the project. Calculated based on FTE count, hourly rate, and duration.',
    examples: ['Internal development team', 'Project managers', 'QA engineers', 'Business analysts', 'Contractors']
  },
  nonLabor: {
    title: 'Non-Labor Costs',
    description: 'All project costs that are not directly related to human resources. Includes both capital and operating expenditures.',
    examples: ['Software licenses', 'Hardware', 'Training materials', 'Travel expenses', 'Third-party services']
  },
  benefits: {
    title: 'Project Benefits',
    description: 'Expected returns or value created by the project. Can be financial (quantifiable in dollars) or non-financial (qualitative improvements).',
    examples: [
      'Financial: Cost savings, revenue increase, productivity gains',
      'Non-Financial: Customer satisfaction, compliance, risk reduction'
    ]
  },
  roi: {
    title: 'ROI (Return on Investment)',
    description: 'Measures the efficiency of the investment. Calculated as: (Benefits - Costs) / Costs × 100%',
    examples: ['Positive ROI = project generates more value than cost', 'Target ROI varies by organization (typically 20-50%)']
  },
  payback: {
    title: 'Payback Period',
    description: 'Time required for the project to "break even" - when cumulative benefits equal total costs.',
    examples: ['Shorter payback = lower risk', 'Enterprise projects typically target 12-36 months']
  }
};

export const HelpTooltip = ({ topic, className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const help = FINANCIAL_HELP[topic];
  
  if (!help) return null;

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        type="button"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
        className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
        aria-label={`Help: ${help.title}`}
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      
      {isVisible && (
        <div className="absolute z-50 w-72 p-4 bg-white border rounded-lg shadow-xl left-6 top-0 text-left">
          <div className="font-semibold text-gray-900 mb-2">{help.title}</div>
          <p className="text-sm text-gray-600 mb-3">{help.description}</p>
          {help.examples && (
            <div>
              <div className="text-xs font-medium text-gray-500 mb-1">Examples:</div>
              <ul className="text-xs text-gray-500 space-y-0.5">
                {help.examples.map((ex, i) => (
                  <li key={i} className="flex items-start gap-1">
                    <span className="text-gray-400">•</span>
                    <span>{ex}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Arrow */}
          <div className="absolute -left-2 top-3 w-2 h-2 bg-white border-l border-b transform rotate-45" />
        </div>
      )}
    </div>
  );
};

export default HelpTooltip;
